// surface-actions.js — the PER-SURFACE ACTION REGISTRY (owner 2026-07-11).
//
// The universal command dock (CaptureDock) runs the global command agent
// (global-agent.js) on every screen. The agent's app-wide vocabulary (navigate,
// open a patient, create a task, …) is the SAME everywhere; but each screen also
// wants the dock to DO that page's own work — change a setting, star a favourite,
// log a due dose. This module is where a surface contributes those extra actions.
//
// ONE mechanism, not a fork of the agent. A surface registers an ARRAY of action
// SPECS keyed by a surface id. The dock looks them up (getSurfaceActions), threads
// them into askGlobalAgent (which widens the CLOSED propose_actions enum + the
// prompt to include them — see global-agent.buildProposeTool), and on Confirm runs
// each kept one through the SAME propose→confirm→run-as-user path a built-in action
// uses. Nothing here bypasses that: each spec is
//
//   { name, describe, params,          // what the MODEL sees (closed vocab + prompt)
//     validate(rawAction) → fields|null // reject anything ill-formed (drop, never stage)
//     execute(action, ctx) → { ok, note } // a THIN call into the page's existing write
//     label(action) → string, icon }    // how the staged chip reads
//
// execute() receives the live page context the dock threads in as `ctx.actionContext`
// (e.g. the visible due items, a reload callback). The writes go through the exact
// helpers the page's own buttons call — savePrefs (settings/favourites) and
// commitDoseTick (the worklist Given button) — so an agent-driven change can never do
// more, or differently, than a click. Propose-only, run-as-the-user, RLS the only lock.
import { loadPrefs, savePrefs } from './medplum-settings.js';
import { commitDoseTick } from '../screens/ward/dose-log.js';
import { listRooms, setMySeat, weekdayCode } from './medplum-rooms.js';
import { setMyDuty } from './medplum-duty.js';
import { clinicTodayKey } from './clinic-time.js';
import { SlidersHorizontal, Star, ClipboardCheck, MapPin, UserCheck } from 'lucide-react';

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const lc = (v) => s(v).toLowerCase();

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS — change a whitelisted setting the user can see on the Settings screen.
// Whitelist ONLY (owner spec): AI assist tier, language, theme, patient-notification
// channels. Identity / feature flags / hospital-wide settings are NOT exposed — those
// are admin-owned or read-only, and stay button-only.
// ══════════════════════════════════════════════════════════════════════════════

// tier: accept "tier 3" / "3" / "three" / "thorough" → 'tier3'.
function normaliseTier(v) {
  const t = lc(v).replace(/\s+/g, '');
  if (/(^|[^0-9])3|three|thorough|deep|largest/.test(t)) return 'tier3';
  if (/(^|[^0-9])1|one|conservative|smallest|fast/.test(t)) return 'tier1';
  if (/(^|[^0-9])2|two|balanced|default|mid/.test(t)) return 'tier2';
  return null;
}
function normaliseLanguage(v) {
  const t = lc(v);
  if (/^(en|eng|english|angre)/.test(t)) return 'en';
  if (/^(hi|hin|hindi)/.test(t) || /हिंदी|हिन्दी/.test(v)) return 'hi';
  return null;
}
function normaliseTheme(v) {
  const t = lc(v);
  if (/dark|night/.test(t)) return 'dark';
  if (/light|day|bright/.test(t)) return 'light';
  if (/system|auto|device/.test(t)) return 'system';
  return null;
}
function normaliseChannel(v) {
  const t = lc(v).replace(/[^a-z]/g, '');
  if (/whatsapp|wa/.test(t)) return 'whatsapp';
  if (/sms|text/.test(t)) return 'sms';
  if (/email|mail/.test(t)) return 'email';
  return null;
}
// on/off/true/false/yes/no/enable/disable → boolean, or null when not a toggle.
function normaliseBool(v) {
  if (v === true || v === false) return v;
  const t = lc(v);
  if (/^(on|true|yes|enable|enabled|1)$/.test(t)) return true;
  if (/^(off|false|no|disable|disabled|0)$/.test(t)) return false;
  return null;
}

const TIER_LABEL = { tier1: 'Tier 1 (Conservative)', tier2: 'Tier 2 (Balanced)', tier3: 'Tier 3 (Thorough)' };
const LANG_LABEL = { en: 'English', hi: 'हिंदी' };
const CHANNEL_LABEL = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email' };
const LANG_KEY = 'healther.opd.lang';

const setPreferenceAction = {
  roles: ['doctor', 'nurse', 'receptionist', 'reception', 'owner', 'admin'],
  name: 'set_preference',
  describe:
    "Change one of the user's OWN settings that is visible on the Settings screen. "
    + "key='ai_tier' value=tier1|tier2|tier3; key='language' value=en|hi; key='theme' value=light|dark|system; "
    + "key='notification' channel=whatsapp|sms|email value=on|off.",
  params: 'key: "ai_tier"|"language"|"theme"|"notification", value, channel? (for notification)',
  validate(a) {
    const key = lc(a.key);
    if (key === 'ai_tier' || key === 'aitier' || key === 'tier') {
      const v = normaliseTier(a.value); if (!v) return null;
      return { key: 'ai_tier', value: v };
    }
    if (key === 'language' || key === 'lang') {
      const v = normaliseLanguage(a.value); if (!v) return null;
      return { key: 'language', value: v };
    }
    if (key === 'theme' || key === 'appearance') {
      const v = normaliseTheme(a.value); if (!v) return null;
      return { key: 'theme', value: v };
    }
    if (key === 'notification' || key === 'notifications') {
      const channel = normaliseChannel(a.channel || a.value);
      const value = normaliseBool(a.value);
      if (!channel || value === null) return null;
      return { key: 'notification', channel, value };
    }
    return null;
  },
  async execute(action, ctx) {
    const onChanged = ctx.actionContext && ctx.actionContext.onChanged;
    if (action.key === 'ai_tier') {
      await savePrefs({ aiTierPolicy: action.value });
      if (onChanged) onChanged();
      return { ok: true, note: `AI assist tier set to ${TIER_LABEL[action.value] || action.value}.` };
    }
    if (action.key === 'language') {
      await savePrefs({ languageDefault: action.value });
      // The Settings page's own picker also writes localStorage + fires opd:lang so the
      // rest of the app reflects the change — do the same so a voice change is identical.
      try { window.localStorage.setItem(LANG_KEY, action.value); } catch (_) { /* private mode */ }
      try { window.dispatchEvent(new CustomEvent('opd:lang', { detail: { lang: action.value } })); } catch (_) {}
      if (onChanged) onChanged();
      return { ok: true, note: `Console language set to ${LANG_LABEL[action.value] || action.value}.` };
    }
    if (action.key === 'theme') {
      // Theme persists THROUGH the theme provider (localStorage + savePrefs({theme}) +
      // the opd:theme event the Settings page listens to) — go through it, don't bypass.
      if (typeof window !== 'undefined' && window.OPDTheme && typeof window.OPDTheme.set === 'function') {
        window.OPDTheme.set(action.value);
      } else {
        await savePrefs({ theme: action.value });
      }
      if (onChanged) onChanged();
      return { ok: true, note: `Appearance set to ${action.value}.` };
    }
    // notification channel toggle
    await savePrefs({ notifications: { [action.channel]: action.value } });
    if (onChanged) onChanged();
    return { ok: true, note: `${CHANNEL_LABEL[action.channel]} patient notifications ${action.value ? 'on' : 'off'}.` };
  },
  label(a) {
    if (a.description) return a.description;
    if (a.key === 'ai_tier') return `Set AI assist tier → ${TIER_LABEL[a.value] || a.value}`;
    if (a.key === 'language') return `Set language → ${LANG_LABEL[a.value] || a.value}`;
    if (a.key === 'theme') return `Set appearance → ${a.value}`;
    if (a.key === 'notification') return `${CHANNEL_LABEL[a.channel]} notifications → ${a.value ? 'on' : 'off'}`;
    return 'Change setting';
  },
  icon: SlidersHorizontal,
  // Native to the Settings screen, but SELF-SUFFICIENT — savePrefs/OPDTheme resolve
  // their own session and global state, so this runs correctly from ANY screen's dock
  // (the cross-surface "direct" tier). No live page selection is needed.
  surfaces: ['settings'],
  selfSufficient: true,
};

// ── FAVOURITES — add a favourite medicine / test / referral to the user's prefs ──
// The SAME per-doctor prefs Basic (savePrefs) the Favourites sections write. Arrays
// replace WHOLESALE in savePrefs (deepMergePrefs), so we read the current list and
// send the FULL appended array — never just the new item.
function departments() {
  try {
    const d = window.OPDBridge && window.OPDBridge.hospital && window.OPDBridge.hospital.departments;
    return Array.isArray(d) ? d.filter((x) => x && x.id && x.name) : [];
  } catch (_) { return []; }
}

const addFavoriteAction = {
  roles: ['doctor', 'nurse', 'owner', 'admin'],
  name: 'add_favorite',
  describe:
    "Add an item to the user's OWN favourites. kind='medication' value=<drug name> (optional dose/frequency/duration); "
    + "kind='test' value=<test name>; kind='referral' value=<department name — must be a real department>.",
  params: 'kind: "medication"|"test"|"referral", value (name), dose?, frequency?, duration?',
  validate(a) {
    const kind = lc(a.kind);
    const value = s(a.value || a.name);
    if (!value) return null;
    if (kind === 'medication' || kind === 'med' || kind === 'medicine') {
      return { kind: 'medication', value, dose: s(a.dose), frequency: s(a.frequency), duration: s(a.duration) };
    }
    if (kind === 'test' || kind === 'investigation' || kind === 'lab') {
      return { kind: 'test', value, region: s(a.region) };
    }
    if (kind === 'referral' || kind === 'department') {
      return { kind: 'referral', value };
    }
    return null;
  },
  async execute(action, ctx) {
    const onChanged = ctx.actionContext && ctx.actionContext.onChanged;
    const prefs = await loadPrefs();
    if (action.kind === 'medication') {
      const list = Array.isArray(prefs.favouriteMeds) ? prefs.favouriteMeds : [];
      if (list.some((m) => lc(m && m.name) === lc(action.value))) return { ok: true, note: `${action.value} is already a favourite medicine.` };
      const next = [...list, { name: action.value, dose: action.dose || '', frequency: action.frequency || '', duration: action.duration || '' }];
      await savePrefs({ favouriteMeds: next });
      if (onChanged) onChanged();
      return { ok: true, note: `Added ${action.value} to favourite medicines.` };
    }
    if (action.kind === 'test') {
      const list = Array.isArray(prefs.favouriteTests) ? prefs.favouriteTests : [];
      if (list.some((t) => lc(t && t.name) === lc(action.value))) return { ok: true, note: `${action.value} is already a favourite test.` };
      const next = [...list, action.region ? { name: action.value, region: action.region } : { name: action.value }];
      await savePrefs({ favouriteTests: next });
      if (onChanged) onChanged();
      return { ok: true, note: `Added ${action.value} to favourite tests.` };
    }
    // referral → resolve the department name to a real hospital department id (the
    // favouriteReferrals row is keyed by departmentId; a name with no match is refused).
    const depts = departments();
    const dept = depts.find((d) => lc(d.name) === lc(action.value) || lc(d.id) === lc(action.value))
      || depts.find((d) => lc(d.name).includes(lc(action.value)) || lc(action.value).includes(lc(d.name)));
    if (!dept) {
      const names = depts.map((d) => d.name).slice(0, 12).join(', ');
      throw new Error(`no department named "${action.value}" — available: ${names || 'none configured'}`);
    }
    const list = Array.isArray(prefs.favouriteReferrals) ? prefs.favouriteReferrals : [];
    if (list.some((r) => lc(r && r.departmentId) === lc(dept.id))) return { ok: true, note: `${dept.name} is already a favourite referral.` };
    const next = [...list, { departmentId: dept.id, departmentName: dept.name, doctorId: '', doctorName: '' }];
    await savePrefs({ favouriteReferrals: next });
    if (onChanged) onChanged();
    return { ok: true, note: `Added ${dept.name} to favourite referrals.` };
  },
  label(a) {
    if (a.description) return a.description;
    const k = a.kind === 'medication' ? 'medicine' : a.kind;
    return `Add favourite ${k}: ${a.value}`;
  },
  icon: Star,
  // Native to Settings, SELF-SUFFICIENT (loadPrefs/savePrefs + the global department
  // list) — runs directly from any dock, so "add X to my favourites" works everywhere.
  surfaces: ['settings'],
  selfSufficient: true,
};

// ══════════════════════════════════════════════════════════════════════════════
// SEATING — set the doctor's OWN room ("I'm in room 5 today") straight from voice.
// The seat is PractitionerRole.location[0]; the write goes through the SAME
// privileged schedule-change Bot the Schedule "My room" picker uses (setMySeat) —
// so a voice change can do no more than a click. SELF-SUFFICIENT: setMySeat +
// listRooms resolve their own session, so it runs from ANY dock. The spoken room
// ("room 5", "OPD 3", "5") is resolved to a real Location at execute() time —
// never invented; an unrecognised room fails loud with the available names.
// ══════════════════════════════════════════════════════════════════════════════
// Match a spoken room to a real Location: exact name, then normalised-equality
// (strip non-alphanumerics), then trailing-number (a bare "5" → "Room 5"), then a
// containment fallback. Returns the room or null (never guesses across two rooms).
function resolveRoom(rooms, spoken) {
  const list = Array.isArray(rooms) ? rooms : [];
  const norm = (v) => lc(v).replace(/[^a-z0-9]/g, '');
  const want = norm(spoken);
  if (!want) return null;
  let hit = list.find((r) => norm(r.name) === want);
  if (hit) return hit;
  const wantNum = (lc(spoken).match(/\d+/) || [])[0];
  if (wantNum) {
    const numMatches = list.filter((r) => {
      const rn = (lc(r.name).match(/\d+/) || [])[0];
      return rn && rn === wantNum;
    });
    if (numMatches.length === 1) return numMatches[0];
  }
  const contains = list.filter((r) => norm(r.name).includes(want) || want.includes(norm(r.name)));
  return contains.length === 1 ? contains[0] : null;
}

// Resolve a spoken day qualifier → a weekday CODE (mon..sun) to scope the seat to,
// or '' for the DEFAULT room ("everywhere"). "today" / "now" → today's weekday; a
// named weekday ("monday", "on tuesdays", "wed") → its code. PURE.
const DAY_WORD = { mon: 'mon', monday: 'mon', tue: 'tue', tues: 'tue', tuesday: 'tue', wed: 'wed', weds: 'wed', wednesday: 'wed', thu: 'thu', thur: 'thu', thurs: 'thu', thursday: 'thu', fri: 'fri', friday: 'fri', sat: 'sat', saturday: 'sat', sun: 'sun', sunday: 'sun' };
function resolveDayScope(raw) {
  const t = lc(raw).replace(/[^a-z]/g, '');
  if (!t) return '';
  if (/^(today|now|tod)$/.test(t)) return weekdayCode(clinicTodayKey());
  for (const word of Object.keys(DAY_WORD)) {
    if (t.indexOf(word) >= 0) return DAY_WORD[word];
  }
  return '';
}
const DAY_LABEL = { mon: 'Mondays', tue: 'Tuesdays', wed: 'Wednesdays', thu: 'Thursdays', fri: 'Fridays', sat: 'Saturdays', sun: 'Sundays' };

// Nudge the header room chip (MyRoomChip) to re-read after a voice seat change.
function notifyRoomChanged() {
  try { window.dispatchEvent(new CustomEvent('opd:room-changed')); } catch (_) { /* non-browser / private mode */ }
}

const setRoomAction = {
  roles: ['doctor', 'owner', 'admin'],
  name: 'set_room',
  describe:
    "Set the user's OWN room / seat (where THEY are sitting). value=<room name or number the "
    + "user said, e.g. 'Room 12', 'OPD 3', '5'>. Use ONLY for the speaker's own seat — not another doctor's. "
    + "day=<'today' when they say today/now, or a weekday like 'monday'/'tuesday' when they name one, "
    + "e.g. 'I'm in room 5 on Mondays'>; OMIT day for their usual/default room (every day). "
    + "To clear it, value='none'.",
  params: 'value: the room name/number the user said (or "none" to clear); day?: "today" or a weekday name if the user scoped it',
  validate(a) {
    const value = s(a.value || a.room || a.name);
    if (!value) return null;
    const day = resolveDayScope(a.day || a.weekday || a.when);
    return { value, ...(day ? { day } : {}) };
  },
  async execute(action, ctx) {
    const onChanged = ctx.actionContext && ctx.actionContext.onChanged;
    // Normalise here too — executeAction runs the RAW proposal (validate() only
    // shapes the staged chip), so a spoken "monday" / "today" must resolve to a code.
    const day = resolveDayScope(action.day || action.weekday || action.when);
    const scopeNote = day ? ` on ${DAY_LABEL[day] || day}` : '';
    if (/^(none|no room|clear|remove|nowhere)$/i.test(action.value.trim())) {
      await setMySeat({ roomId: '', ...(day ? { day } : {}) });
      if (onChanged) onChanged();
      notifyRoomChanged();
      return { ok: true, note: day ? `Cleared your room${scopeNote}.` : 'Cleared your default room.' };
    }
    const rooms = await listRooms();
    const room = resolveRoom(rooms, action.value);
    if (!room) {
      const names = rooms.map((r) => r.name).slice(0, 12).join(', ');
      throw new Error(`no room matching "${action.value}"${names ? ` — available: ${names}` : ' — no rooms are set up yet'}. Add it in Settings → Rooms first.`);
    }
    await setMySeat({ roomId: room.id, ...(day ? { day } : {}) });
    if (onChanged) onChanged();
    notifyRoomChanged();
    return { ok: true, note: day ? `You’re now in ${room.name}${scopeNote}.` : `${room.name} is now your default room.` };
  },
  label(a) {
    if (a.description) return a.description;
    const day = a.day || '';
    const scope = day ? ` (${DAY_LABEL[day] || day})` : '';
    if (/^(none|no room|clear|remove|nowhere)$/i.test(String(a.value || '').trim())) return `Clear my room${scope}`;
    return `Set my room → ${a.value}${scope}`;
  },
  icon: MapPin,
  // Native to Schedule, SELF-SUFFICIENT (setMySeat/listRooms resolve their own
  // session), so "I'm in room 5 today" works from any dock.
  surfaces: ['schedule'],
  selfSufficient: true,
};

// ══════════════════════════════════════════════════════════════════════════════
// DUTY — set the doctor's OWN presence ("I'm in" / "I'm out today") from voice.
// The explicit toggle behind Live Queue Engine §3: it writes PractitionerRole.on-duty
// through the SAME privileged schedule-change Bot the "I'm in / I'm out" header
// button uses (setMyDuty) — so a voice change can do no more than a click. When
// present the whole department queue drains through the present doctors; when the
// doctor toggles out, the boards stop guiding patients to them. SELF-SUFFICIENT
// (setMyDuty resolves its own session), so it runs from ANY dock. An optional room
// ("I'm in room 5") rides along as a presence hint.
// ══════════════════════════════════════════════════════════════════════════════
function normaliseDuty(v) {
  const t = lc(v);
  if (/^(on|in|available|present|here|start|start(ing)?|clock ?in|checking? in|back)/.test(t)) return 'on';
  if (/^(off|out|away|leaving|leave|gone|clock ?out|done for|end(ing)?|sign(ed)? off)/.test(t)) return 'off';
  return null;
}

const setDutyAction = {
  roles: ['doctor', 'owner', 'admin'],
  name: 'set_duty',
  describe:
    "Set the user's OWN on-duty presence for the queue. status='on' when they say they're in / "
    + "here / starting / back; status='off' when they're out / leaving / done for the day. "
    + "Optional room=<the room they named> when they mention where they're sitting.",
  params: 'status: "on"|"off", room?: the room name/number if the user said one',
  validate(a) {
    const status = normaliseDuty(a.status || a.value || a.duty);
    if (!status) return null;
    const room = s(a.room || a.location);
    return { status, ...(room ? { room } : {}) };
  },
  async execute(action, ctx) {
    const onChanged = ctx.actionContext && ctx.actionContext.onChanged;
    await setMyDuty({ status: action.status, ...(action.room ? { room: action.room } : {}) });
    if (onChanged) onChanged();
    return {
      ok: true,
      note: action.status === 'on'
        ? `You’re now ON duty${action.room ? ` in ${action.room}` : ''} — the queue will route patients to you.`
        : 'You’re now OFF duty — the queue will stop guiding patients to you.',
    };
  },
  label(a) {
    if (a.description) return a.description;
    return a.status === 'on'
      ? `Set me ON duty${a.room ? ` → ${a.room}` : ''}`
      : 'Set me OFF duty';
  },
  icon: UserCheck,
  // Native to the Queue/Schedule surfaces, SELF-SUFFICIENT (setMyDuty resolves its
  // own session), so "I'm in" / "I'm heading out" works from any dock.
  surfaces: ['schedule', 'today'],
  selfSufficient: true,
};

// ══════════════════════════════════════════════════════════════════════════════
// WARD WORKLIST — log a due medication dose straight from the worklist by voice.
// The REAL dose-log write the "Given" button performs (commitDoseTick → a
// MedicationAdministration keyed by the row's exact slotKey), so the due-engine
// marks the slot done and the row LEAVES the list on the next refetch.
// ══════════════════════════════════════════════════════════════════════════════
const DOSE_OUTCOMES = ['given', 'held', 'refused', 'unavailable'];

const logDoseAction = {
  roles: ['doctor', 'nurse', 'owner', 'admin'],
  name: 'log_dose',
  describe:
    'Log a DUE medication dose from the worklist. Resolve the patient + the matching due item '
    + 'from the CURRENT SCREEN CONTEXT list and copy that item\'s slotKey VERBATIM. If several due items match, ask which; '
    + 'if none matches, say so and offer the nearest — never invent a slotKey. outcome defaults to "given".',
  params: 'slotKey (copy verbatim from the worklist context), outcome: "given"|"held"|"refused"|"unavailable" (default given), reason?, note?',
  validate(a) {
    const slotKey = s(a.slotKey);
    if (!slotKey) return null;
    const outcome = DOSE_OUTCOMES.includes(lc(a.outcome)) ? lc(a.outcome) : 'given';
    return { slotKey, outcome, reason: s(a.reason), note: s(a.note) };
  },
  async execute(action, ctx) {
    const items = (ctx.actionContext && Array.isArray(ctx.actionContext.items)) ? ctx.actionContext.items : [];
    const item = items.find((i) => i && i.slotKey === action.slotKey);
    if (!item) {
      throw new Error('that scheduled dose is no longer on the worklist — it may already be logged. Refresh and try again.');
    }
    await commitDoseTick(item, {
      outcome: action.outcome,
      ...(action.reason ? { reason: action.reason } : {}),
      ...(action.note ? { note: action.note } : {}),
      ...(ctx.sourceQuote ? { aiOrigin: { sourceQuote: ctx.sourceQuote } } : {}),
    });
    // The SAME success path the Given button runs — animates the row out + refetches
    // the worklist so the item leaves the list.
    try { if (ctx.actionContext && typeof ctx.actionContext.onDoseLogged === 'function') ctx.actionContext.onDoseLogged(item, action.outcome); } catch (_) {}
    const who = (item.patient && item.patient.displayName) || 'patient';
    const drug = (item.order && item.order.name) || 'dose';
    const verb = action.outcome === 'given' ? 'given' : action.outcome;
    return { ok: true, note: `Logged ${drug} ${verb} for ${who} — removed from the worklist.` };
  },
  label(a) {
    if (a.description) return a.description;
    return `Log dose (${a.outcome || 'given'})`;
  },
  icon: ClipboardCheck,
  // Native to the Ward worklist and PAGE-BOUND — execute() needs the live visible due
  // items (ctx.actionContext.items) to resolve the exact slotKey, so it can only run on
  // that screen. From elsewhere the dock escalates via navigate-then-replay (below).
  surfaces: ['ward-worklist'],
  selfSufficient: false,
};

// ══════════════════════════════════════════════════════════════════════════════
// THE GLOBAL CATALOG — every surface action, each tagged with the surface(s) it is
// NATIVE to and whether it is SELF-SUFFICIENT (can execute from any dock) or
// PAGE-BOUND (needs the target screen's live data). The dock reads this to give the
// model a LOCAL-FIRST view of the current screen's actions PLUS a cross-surface
// escalation tier (owner design rule 2026-07-11):
//   • local            = native here → preferred, run directly.
//   • remoteDirect      = native elsewhere but self-sufficient → offered here too,
//                         run directly (e.g. "add a favourite med" from the worklist).
//   • remotePageBound   = native elsewhere AND needs that screen → NOT run here; the
//                         model escalates via navigate_to { thenAsk } (navigate-then-
//                         replay) so it executes on arrival.
// Honest refusal is reserved for a request that matches NOTHING in this catalog.
// ══════════════════════════════════════════════════════════════════════════════
const CATALOG = [setPreferenceAction, addFavoriteAction, setRoomAction, setDutyAction, logDoseAction];

// Friendly surface names for the "that's done on <X>" escalation copy, and the nav
// target key the model uses to go there (mirrors global-agent NAV_TARGETS keys).
export const SURFACE_LABELS = { settings: 'Settings', schedule: 'Schedule', 'ward-worklist': 'the Ward worklist' };
const SURFACE_NAV_KEY = { settings: 'settings', schedule: 'schedule', 'ward-worklist': 'ward-worklist' };
export function surfaceNavKey(surfaceId) { return SURFACE_NAV_KEY[s(surfaceId)] || null; }

/** The whole catalog (all surfaces). */
export function getCatalog() { return CATALOG; }

/**
 * Partition the catalog for a given current surface into the three tiers above.
 */
export function agentActionsForSurface(surfaceId, roleOrUser = null) {
  const surface = s(surfaceId);
  const role = lc(roleOrUser && typeof roleOrUser === 'object' ? roleOrUser.role : roleOrUser);
  const local = [];
  const remoteDirect = [];
  const remotePageBound = [];
  for (const spec of CATALOG) {
    if (role && Array.isArray(spec.roles) && !spec.roles.includes(role)) continue;
    const native = (spec.surfaces || []).includes(surface);
    if (native) local.push(spec);
    else if (spec.selfSufficient) remoteDirect.push(spec);
    else remotePageBound.push(spec);
  }
  return { local, remoteDirect, remotePageBound };
}

/**
 * The specs the model may STAGE + the dock may EXECUTE on this surface — local plus
 * the self-sufficient remote ones (which run directly). Page-bound remote actions are
 * NOT here (they escalate via navigate_to). Each carries `remote` + `nativeLabel` so
 * the prompt/chip can say where it lives.
 */
export function stageableActionsForSurface(surfaceId, roleOrUser = null) {
  const { local, remoteDirect } = agentActionsForSurface(surfaceId, roleOrUser);
  const tag = (spec, remote) => ({ ...spec, remote, nativeLabel: SURFACE_LABELS[(spec.surfaces || [])[0]] || null });
  return [...local.map((sp) => tag(sp, false)), ...remoteDirect.map((sp) => tag(sp, true))];
}

/**
 * The page-bound actions that live on OTHER surfaces — for the prompt's escalation
 * note ("dose logging is done on the Ward worklist — go there?"). Each has the nav key.
 */
export function escalationsForSurface(surfaceId, roleOrUser = null) {
  const { remotePageBound } = agentActionsForSurface(surfaceId, roleOrUser);
  return remotePageBound.map((spec) => ({
    name: spec.name,
    describe: spec.describe,
    surfaceLabel: SURFACE_LABELS[(spec.surfaces || [])[0]] || 'another screen',
    navKey: surfaceNavKey((spec.surfaces || [])[0]),
  }));
}

/**
 * A one-screen text digest of the surface's live entities for the agent prompt —
 * e.g. the ward worklist's currently-due items with their slotKeys, so the model can
 * resolve "log paracetamol for Ramesh" to a specific row. Returns null when the
 * surface has no such context.
 */
export function describeSurfaceContext(surfaceId, actionContext) {
  const surface = s(surfaceId);
  if (surface === 'ward-worklist') {
    const items = (actionContext && Array.isArray(actionContext.items)) ? actionContext.items : [];
    const doses = items.filter((i) => i && i.kind === 'dose' && i.order);
    if (!doses.length) return 'No medication doses are currently due on the worklist.';
    const lines = doses.slice(0, 60).map((i) => {
      const o = i.order || {};
      const p = i.patient || {};
      const when = i.prn ? 'PRN' : (i.dueAt ? new Date(i.dueAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
      const bits = [o.name, o.doseText, o.route].map(s).filter(Boolean).join(' ');
      return `slotKey=${i.slotKey} | ${bits} for ${p.displayName || 'patient'}${p.id ? ` (Patient/${p.id})` : ''}${i.bedLabel ? ` [${i.bedLabel}]` : ''} — ${i.status || 'due'} ${when}`;
    });
    return `DUE MEDICATION DOSES on the worklist right now (${doses.length}):\n${lines.join('\n')}`;
  }
  return null;
}

// Test surface for node/vitest harnesses.
export const __surfaceActionsTest = {
  setPreferenceAction, addFavoriteAction, setRoomAction, logDoseAction,
  normaliseTier, normaliseLanguage, normaliseTheme, normaliseChannel, normaliseBool, resolveRoom, resolveDayScope,
  CATALOG, agentActionsForSurface, stageableActionsForSurface, escalationsForSurface,
};
